import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flag, Shield, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export function AdminReportsTab() {
  const token = sessionStorage.getItem('session_token');
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reviewingReport, setReviewingReport] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [gdprConsents, setGdprConsents] = useState([]);
  const [showGdpr, setShowGdpr] = useState(false);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports`, { credentials: 'include', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setReports(Array.isArray(data) ? data : []); }
      setReportsLoading(false);
    } catch { setReportsLoading(false); }
  };

  const handleReviewReport = async (reportId) => {
    if (!reviewAction) { toast.error('Select an action'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/${reportId}/review`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: reviewAction, admin_notes: reviewNotes }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(`Report ${reviewAction === 'dismiss' ? 'dismissed' : 'actioned'}`);
      setReviewingReport(null); setReviewAction(''); setReviewNotes('');
      loadReports();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="admin-reports-content">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold font-heading">Member Reports</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{reports.filter(r => r.status === 'pending').length} pending</span>
          <Button size="sm" variant="outline" data-testid="view-gdpr-btn"
            onClick={async () => {
              try {
                const res = await fetch(`${BACKEND_URL}/api/auth/gdpr-consents`, { credentials: 'include', headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) { setGdprConsents(await res.json()); setShowGdpr(true); }
              } catch {}
            }}>
            <Shield className="h-3.5 w-3.5 mr-1.5" /> GDPR Consent Log
          </Button>
        </div>
      </div>

      {reportsLoading ? (
        <div className="text-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy mx-auto" /></div>
      ) : reports.length === 0 ? (
        <Card className="p-10 text-center">
          <Flag className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No reports submitted yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <Card key={r.report_id} data-testid={`report-card-${r.report_id}`}
              className={`p-5 border ${r.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                      r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      r.status === 'dismissed' ? 'bg-slate-100 text-slate-500' : 'bg-red-100 text-red-700'
                    }`}>{r.status}</span>
                    {r.action_taken === 'suspend' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">SUSPENDED</span>}
                    <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">
                    <span className="text-slate-500 font-normal">Reported:</span> {r.reported_user_name} <span className="text-slate-400 text-xs">({r.reported_user_email})</span>
                  </p>
                  <p className="text-sm text-slate-600 mb-1"><span className="text-slate-500">Reason:</span> {r.reason}</p>
                  {r.details && <p className="text-sm text-slate-500 italic">"{r.details}"</p>}
                  <p className="text-xs text-slate-400 mt-1">Reported by: {r.reporter_name} | Context: {r.context_type || 'N/A'}</p>
                  {r.admin_notes && (
                    <p className="text-xs text-slate-500 mt-2 p-2 bg-slate-50 rounded"><span className="font-semibold">Admin notes:</span> {r.admin_notes}</p>
                  )}
                </div>
                {r.status === 'pending' && (
                  <Button size="sm" variant="outline" data-testid={`review-report-${r.report_id}`}
                    onClick={() => { setReviewingReport(r); setReviewAction(''); setReviewNotes(''); }} className="flex-shrink-0">
                    Review
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!reviewingReport} onOpenChange={() => setReviewingReport(null)}>
        <DialogContent className="max-w-md" data-testid="review-report-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><Shield className="h-4 w-4" /> Review Report</DialogTitle></DialogHeader>
          {reviewingReport && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <p><strong>Reported:</strong> {reviewingReport.reported_user_name}</p>
                <p><strong>Reason:</strong> {reviewingReport.reason}</p>
                {reviewingReport.details && <p><strong>Details:</strong> {reviewingReport.details}</p>}
                <p className="text-xs text-slate-400">By {reviewingReport.reporter_name} on {new Date(reviewingReport.created_at).toLocaleString()}</p>
              </div>
              <div>
                <Label className="mb-1.5 block">Action</Label>
                <Select value={reviewAction} onValueChange={setReviewAction}>
                  <SelectTrigger data-testid="review-action-select"><SelectValue placeholder="Select action" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dismiss">Dismiss (no action needed)</SelectItem>
                    <SelectItem value="warn">Warn (note only)</SelectItem>
                    <SelectItem value="suspend">Suspend User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Admin Notes</Label>
                <Textarea data-testid="review-notes-input" placeholder="Internal notes about this review..."
                  value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} className="min-h-16" />
              </div>
              {reviewAction === 'suspend' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                  This will immediately suspend the user's account and log them out. They will not be able to access the platform.
                </div>
              )}
              <Button data-testid="confirm-review-btn" onClick={() => handleReviewReport(reviewingReport.report_id)}
                disabled={!reviewAction}
                className={`w-full ${reviewAction === 'suspend' ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-navy hover:bg-brand-dark'} text-white`}>
                {reviewAction === 'dismiss' ? 'Dismiss Report' : reviewAction === 'warn' ? 'Save Warning' : reviewAction === 'suspend' ? 'Suspend User' : 'Select Action'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showGdpr} onOpenChange={setShowGdpr}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="gdpr-consent-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><Shield className="h-4 w-4" /> GDPR Consent Log</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            {gdprConsents.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No consent records yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {gdprConsents.map(c => (
                  <div key={c.consent_id} data-testid={`gdpr-row-${c.consent_id}`} className="py-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-800">{c.name} <span className="font-normal text-slate-400 text-xs">({c.email})</span></span>
                      <span className="text-xs text-slate-400">{new Date(c.consented_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-500">{c.details}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">IP: {c.ip_address}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
